import { Map } from 'immutable';
import GroupMembership from 'entities/GroupMembership';
import { ClassMembershipFetchOptions } from 'clients/api/classMemberSearch';
import { fetchMembership, fetchMemberships } from 'clients/api/classMemberSearch';
import { PushEvents } from 'pusher/PusherConstants';
import { Backbone$Id } from 'types/typescript/Backbone';

export type PeopleTable = {
  page: number;
  loading: boolean;
  allLoaded: boolean;
  memberships: GroupMembership[];
  selectedMemberships: Map<Backbone$Id, GroupMembership>;
  groupUuid: string | null;
  error: Error | null;
};

export type State = Map<Backbone$Id, PeopleTable>;

export const initialState: State = Map();

export default function reducer(state: State = initialState, action: any): State {
  switch (action.type) {
    case 'peopleTables/INITIALIZE_TABLE': {
      return state.set(action.groupId, {
        page: 1,
        loading: false,
        allLoaded: false,
        memberships: [],
        error: null,
        selectedMemberships: Map(),
        groupUuid: action.groupUuid,
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIPS_REQUEST': {
      const table = state.get(action.groupId);

      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIPS_REQUEST missing table';
      }

      return state.set(action.groupId, {
        ...table,
        loading: true,
        error: null,
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIPS_ERROR': {
      const table = state.get(action.groupId);
      if (!table) {
        throw 'Error finding PeopleTable in LOAD_MEMBERSHIPS_ERROR';
      }

      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIPS_ERROR missing table';
      }

      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIPS_ERROR missing table';
      }

      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIPS_ERROR missing table';
      }

      return state.set(action.groupId, {
        ...table,
        loading: false,
        error: action.error,
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS': {
      const table = state.get(action.groupId);
      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS missing table';
      }
      return state.set(action.groupId, {
        ...table,
        loading: false,
        error: null,
        page: action.options.page,
        allLoaded: action.memberships.length < action.options.perPage,
        memberships:
          action.options.page === 1
            ? action.memberships
            : Array<GroupMembership>().concat(table.memberships, action.memberships),
      });
    }

    case 'peopleTables/LOAD_MEMBERSHIP_SUCCESS': {
      const { groupId, membership, options } = action;

      const table = state.get(groupId);
      if (!table) {
        throw 'peopleTables/LOAD_MEMBERSHIP_SUCCESS missing table';
      }
      const update = options && options.update;

      let updated = false;

      // updating memberships
      const memberships = table.memberships.map(m => {
        if (m.id !== membership.id) {
          return m;
        }

        updated = true;
        return membership;
      });

      if (!update && !updated) {
        memberships.push(membership);
      }

      // updating selected memberships
      const membershipId: Backbone$Id = membership.id;
      if (table.selectedMemberships.has(membershipId)) {
        table.selectedMemberships = table.selectedMemberships.set(membershipId, membership);
      }

      return state.set(action.groupId, { ...table, memberships });
    }

    case 'peopleTables/SELECT_MEMBERSHIP': {
      const table = state.get(action.groupId);
      if (!table) {
        throw 'peopleTables/SELECT_MEMBERSHIP missing table';
      }
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.set(action.membership.id, action.membership),
      });
    }

    case 'peopleTables/UNSELECT_MEMBERSHIP': {
      const table = state.get(action.groupId);
      if (!table) {
        throw 'peopleTables/UNSELECT_MEMBERSHIP missing table';
      }
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.delete(action.membership.id),
      });
    }

    case 'peopleTables/UNSELECT_ALL_MEMBERSHIP': {
      const table = state.get(action.groupId);
      if (!table) {
        throw 'peopleTables/UNSELECT_ALL_MEMBERSHIP missing table';
      }
      return state.set(action.groupId, {
        ...table,
        selectedMemberships: table.selectedMemberships.clear(),
      });
    }

    case 'peopleTables/REMOVE_MEMBERS': {
      const removedMembers = action.userIds;
      const table = state.get(action.groupId);
      if (!table) {
        throw 'peopleTables/REMOVE_MEMBERS missing table';
      }

      const memberships = table.memberships.filter(
        membership => !removedMembers.includes(membership.getUserId())
      );

      const selectedMemberships = table.selectedMemberships.filter(membership => {
        if (!membership) {
          return false;
        }

        return !removedMembers.includes(membership.getUserId());
      }) as Map<Backbone$Id, GroupMembership>;

      return state.set(action.groupId, {
        ...table,
        memberships,
        selectedMemberships,
      });
    }

    case PushEvents.CHANGESET_CREATE_CLASSMEMBERSHIP: {
      const groupUuid = action.changes[0].content.group_uuid;
      const groupId = state.findKey(value => value?.groupUuid === groupUuid);

      if (groupId) {
        // if a user was direct added to a group that has been initialized in
        // the redux cache, invalidate the cache so that the list of memberships
        return state.delete(groupId);
      }

      return state;
    }

    default: {
      return state;
    }
  }
}

type InitializeTableAction = {
  type: 'peopleTables/INITIALIZE_TABLE';
  groupId: Backbone$Id;
  groupUuid: string;
};

export function initializeTable(groupId: Backbone$Id, groupUuid: string): InitializeTableAction {
  return {
    type: 'peopleTables/INITIALIZE_TABLE',
    groupId,
    groupUuid,
  };
}

type SelectMembershipAction = {
  type: 'peopleTables/SELECT_MEMBERSHIP';
  membership: GroupMembership;
  groupId: Backbone$Id;
};

export function selectMembership(
  groupId: Backbone$Id,
  membership: GroupMembership
): SelectMembershipAction {
  return {
    type: 'peopleTables/SELECT_MEMBERSHIP',
    membership,
    groupId,
  };
}

type UnselectMembershipAction = {
  type: 'peopleTables/UNSELECT_MEMBERSHIP';
  membership: GroupMembership;
  groupId: Backbone$Id;
};

export function unselectMembership(
  groupId: Backbone$Id,
  membership: GroupMembership
): UnselectMembershipAction {
  return {
    type: 'peopleTables/UNSELECT_MEMBERSHIP',
    membership,
    groupId,
  };
}

type UnselectAllMembershipAction = {
  type: 'peopleTables/UNSELECT_ALL_MEMBERSHIP';
  groupId: Backbone$Id;
};

export function unselectAllMembership(groupId: Backbone$Id): UnselectAllMembershipAction {
  return {
    type: 'peopleTables/UNSELECT_ALL_MEMBERSHIP',
    groupId,
  };
}

export type LoadMembershipsRequestAction = {
  type: 'peopleTables/LOAD_MEMBERSHIPS_REQUEST';
  groupId: Backbone$Id;
  options: any;
};

export function loadMembershipsRequest(
  groupId: Backbone$Id,
  options: any
): LoadMembershipsRequestAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIPS_REQUEST',
    groupId,
    options,
  };
}

export type loadMembershipErrorAction = {
  type: 'peopleTables/LOAD_MEMBERSHIPS_ERROR';
  groupId: Backbone$Id;
  error: Error;
  options: any;
};

export function loadMembershipsError(
  error: Error,
  groupId: Backbone$Id,
  options: ClassMembershipFetchOptions
): loadMembershipErrorAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIPS_ERROR',
    groupId,
    options,
    error,
  };
}

export type LoadMembershipsSuccessAction = {
  type: 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS';
  memberships: any;
  groupId: Backbone$Id;
  options: any;
};

export function loadMembershipsSuccess(
  groupId: Backbone$Id,
  memberships: GroupMembership[],
  options: ClassMembershipFetchOptions
): LoadMembershipsSuccessAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIPS_SUCCESS',
    memberships,
    groupId,
    options,
  };
}

export type RemoveMembersAction = {
  type: 'peopleTables/REMOVE_MEMBERS';
  userIds: Backbone$Id[];
  groupId: Backbone$Id;
};

export function removeMembers(groupId: Backbone$Id, userIds: Backbone$Id[]): RemoveMembersAction {
  return {
    type: 'peopleTables/REMOVE_MEMBERS',
    userIds,
    groupId,
  };
}

export type LoadMembershipOptions = {
  update?: boolean; // when true, will ignore if not in store.
};

export type LoadMembershipRequestAction = {
  type: 'peopleTables/LOAD_MEMBERSHIP_REQUEST';
  groupId: Backbone$Id;
  userId: Backbone$Id;
  options: LoadMembershipOptions;
};

export function loadMembershipRequest(
  groupId: Backbone$Id,
  userId: Backbone$Id,
  options: LoadMembershipOptions
): LoadMembershipRequestAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIP_REQUEST',
    groupId,
    userId,
    options,
  };
}

export type LoadMembershipSuccessAction = {
  type: 'peopleTables/LOAD_MEMBERSHIP_SUCCESS';
  membership: GroupMembership;
  groupId: Backbone$Id;
  options: LoadMembershipOptions;
};

export function loadMembershipSuccess(
  groupId: Backbone$Id,
  membership: GroupMembership,
  options: LoadMembershipOptions
): LoadMembershipSuccessAction {
  return {
    type: 'peopleTables/LOAD_MEMBERSHIP_SUCCESS',
    membership,
    options,
    groupId,
  };
}

export function loadMembership(
  groupId: Backbone$Id,
  userId: Backbone$Id,
  options: LoadMembershipOptions
) {
  return async (dispatch: Function) => {
    dispatch(loadMembershipRequest(groupId, userId, options));

    try {
      const membership = await fetchMembership(groupId, userId);
      dispatch(loadMembershipSuccess(groupId, membership, options));
    } catch (e) {
      console.error('loadMembership failure', e);
    }
  };
}

export function loadMemberships(groupId: Backbone$Id, options: ClassMembershipFetchOptions) {
  return async (dispatch: Function) => {
    dispatch(loadMembershipsRequest(groupId, options));

    try {
      const memberships = await fetchMemberships(groupId, options);
      dispatch(loadMembershipsSuccess(groupId, memberships, options));
    } catch (e) {
      dispatch(loadMembershipsError(e, groupId, options));
      console.error('loadMemberships failure', e);
    }
  };
}

export type PeopleTablesAction =
  | RemoveMembersAction
  | SelectMembershipAction
  | UnselectMembershipAction
  | UnselectAllMembershipAction
  | LoadMembershipsRequestAction
  | LoadMembershipsSuccessAction
  | LoadMembershipSuccessAction
  | LoadMembershipRequestAction;
